import React, { useContext, useEffect, useState } from 'react';
import { FaPhoneAlt } from 'react-icons/fa';

import api from '../../api';
import { AuthContext } from '../../context/AuthContext';
import useApi from '../../hooks/useApi';
import Alert from '../common/Alert';
import Pagination from '../common/Pagination';

const emptyFilters = { division: '', houseNo: '', name: '', ownershipType: '', vehicleCount: '', sortBy: 'DIVISION', sortDirection: 'ASC' };

export default function Reports() {
  const { session } = useContext(AuthContext);
  const superAdmin = session.roles.includes('SUPER_ADMIN');
  const { get } = useApi();
  const [tab, setTab] = useState('vehicles');
  const [communityId, setCommunityId] = useState(session.communityId || '');
  const [communities, setCommunities] = useState([]);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (superAdmin) get('/api/communities/active').then(setCommunities).catch(error => setError(error.message));
  }, [superAdmin, get]);

  useEffect(() => setRows([]), [tab, communityId]);

  const params = () => ({
    ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '')),
    ...(communityId ? { communityId } : {})
  });

  const view = async () => {
    if (superAdmin && !communityId) return setError('Select a community first.');
    try {
      setBusy(true);
      setRows(await get(`/api/reports/${tab}`, { params: params() }));
      setPage(0);
      setError('');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const exportReport = async format => {
    if (superAdmin && !communityId) return setError('Select a community first.');
    try {
      setBusy(true);
      const response = await api.get(`/api/reports/${tab}.${format}`, { params: params(), responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${tab}-report.${format}`;
      anchor.click();
      URL.revokeObjectURL(url);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to generate report.');
    } finally {
      setBusy(false);
    }
  };

  const updateFilter = (name, value) => setFilters(current => ({ ...current, [name]: value }));
  const sortBy = async column => {
    const direction = filters.sortBy === column && filters.sortDirection === 'ASC' ? 'DESC' : 'ASC';
    const nextFilters = { ...filters, sortBy: column, sortDirection: direction };
    setFilters(nextFilters);
    try {
      setBusy(true);
      setRows(await get(`/api/reports/${tab}`, { params: { ...Object.fromEntries(Object.entries(nextFilters).filter(([, value]) => value !== '')), ...(communityId ? { communityId } : {}) } }));
      setPage(0); setError('');
    } catch (requestError) { setError(requestError.message); } finally { setBusy(false); }
  };

  return <div className="engagement-page">
    <div className="engagement-heading"><div><span>Community intelligence</span><h2>Resident reports</h2><p>Review filtered vehicle and member details before exporting a branded PDF or CSV.</p></div></div>
    {error && <Alert type="danger" message={error} />}
    <nav className="nav nav-pills gap-2">
      <button className={`nav-link ${tab === 'vehicles' ? 'active' : ''}`} onClick={() => setTab('vehicles')}>Vehicle details</button>
      <button className={`nav-link ${tab === 'members' ? 'active' : ''}`} onClick={() => setTab('members')}>Member details</button>
    </nav>
    <section className="settings-panel" style={{ maxWidth: 'none' }}>
      <div className="settings-grid">
        {superAdmin && <label>Community<select className="form-select" value={communityId} onChange={event => setCommunityId(event.target.value)}><option value="">Select community</option>{communities.map(community => <option key={community.id} value={community.id}>{community.displayName || community.name}</option>)}</select></label>}
        <label>Division<input className="form-control" value={filters.division} onChange={event => updateFilter('division', event.target.value)} /></label>
        <label>House number<input className="form-control" value={filters.houseNo} onChange={event => updateFilter('houseNo', event.target.value)} /></label>
        <label>Resident/member name<input className="form-control" value={filters.name} onChange={event => updateFilter('name', event.target.value)} /></label>
        <label>Occupancy<select className="form-select" value={filters.ownershipType} onChange={event => updateFilter('ownershipType', event.target.value)}><option value="">All</option><option value="OWNER">Owner</option><option value="TENANT">Tenant</option></select></label>
        {tab === 'vehicles' && <label>Vehicle count<select className="form-select" value={filters.vehicleCount} onChange={event => updateFilter('vehicleCount', event.target.value)}><option value="">Any count</option><option value="1">Exactly one</option><option value="2">Two or more</option><option value="3">Three or more</option></select></label>}
      </div>
      <div className="d-flex flex-wrap gap-2">
        <button className="btn btn-primary" disabled={busy} onClick={view}>{busy ? 'Loading...' : 'View report'}</button>
        <button className="btn btn-outline-primary" disabled={busy} onClick={() => exportReport('csv')}>Export CSV</button>
        <button className="btn btn-outline-danger" disabled={busy} onClick={() => exportReport('pdf')}>Export PDF</button>
        <button className="btn btn-light" onClick={() => { setFilters(emptyFilters); setRows([]); }}>Clear filters</button>
      </div>
    </section>
    <ReportTable tab={tab} rows={rows} filters={filters} onSort={sortBy} page={page} onPage={setPage} />
  </div>;
}

function CallLink({ number, label }) {
  if (!number) return <span className="text-muted">-</span>;
  const dialNumber = String(number).replace(/[^+\d]/g, '');
  return <a href={`tel:${dialNumber}`} className="d-inline-flex align-items-center gap-1 text-decoration-none" aria-label={`Call ${label || number}`} title={`Call ${number}`}><FaPhoneAlt aria-hidden="true" /> <span>{number}</span></a>;
}

function ReportTable({ tab, rows, filters, onSort, page, onPage }) {
  if (!rows.length) return <div className="engagement-empty">Apply filters and select View report.</div>;
  const pageSize = 15; const totalPages = Math.ceil(rows.length / pageSize); const visibleRows = rows.slice(page * pageSize, page * pageSize + pageSize);
  const sortHeader = (label, column) => <button type="button" className="btn btn-link p-0 text-decoration-none fw-semibold" onClick={() => onSort(column)}>{label}{filters.sortBy === column ? ` ${filters.sortDirection === 'ASC' ? '↑' : '↓'}` : ''}</button>;
  return <section className="engagement-list">
    <header><h3>{rows.length} report rows</h3></header>
    <div className="table-responsive"><table className="table table-striped align-middle">
      <thead>{tab === 'vehicles'
        ? <tr><th>{sortHeader('Division','DIVISION')}</th><th>{sortHeader('House no.','HOUSE')}</th><th>Resident</th><th>Occupancy</th><th>Contact</th><th>{sortHeader('Count','VEHICLE_COUNT')}</th><th>Vehicle type</th><th>Vehicle number</th><th>Status</th></tr>
        : <tr><th>{sortHeader('Division','DIVISION')}</th><th>{sortHeader('House no.','HOUSE')}</th><th>Resident</th><th>Member</th><th>Relation</th><th>Occupancy</th><th>Contacts</th><th>Details</th></tr>}</thead>
      <tbody>{visibleRows.map((row, index) => tab === 'vehicles'
        ? <tr key={`${row.userId}-${row.vehicleNo}-${index}`}><td>{row.division}</td><td>{row.houseNo}</td><td>{row.residentName}</td><td>{row.ownershipType}</td><td><CallLink number={row.mobileNo} label={row.residentName} /><small className="d-block">{row.email}</small></td><td>{row.vehicleCount}</td><td>{row.vehicleType || '-'}</td><td>{row.vehicleNo || 'No active vehicle'}</td><td>{row.vehicleStatus || '-'}</td></tr>
        : <tr key={row.memberId}><td>{row.division}</td><td>{row.houseNo}</td><td>{row.residentName}</td><td>{row.memberName}</td><td>{row.relation}</td><td>{row.ownershipType}</td><td><CallLink number={row.mobileNo} label={row.memberName} /><small className="d-block">Resident: <CallLink number={row.residentMobileNo} label={row.residentName} /></small></td><td>{row.gender || '-'} / age {row.age ?? '-'}<small className="d-block">{row.bloodGroup || 'Blood group not set'}</small></td></tr>)}</tbody>
    </table></div>
    {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPage} />}
  </section>;
}
